import { X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { cn } from "@/lib/utils";

/**
 * One date window — a From and a To that know about each other.
 *
 * Every screen that filters by a window was writing the pair out again: two
 * loose `DatePicker`s on each audit-log tab, two native `<input type="date">`
 * on the usage toolbar. The copies had already drifted — different heights,
 * some with a label and some with only a placeholder, and none of them stopped
 * a reader choosing a From after their To, which asks the server for a window
 * that cannot hold anything.
 *
 * What this adds over two loose pickers:
 *
 * - **The bounds hold each other.** From cannot pass To, and To cannot fall
 *   behind From — the calendar greys the impossible days rather than letting
 *   the request go out and come back empty.
 * - **The labels stay on screen.** A placeholder disappears the moment a date
 *   is picked, and "12 Aug 2026 – 15 Aug 2026" with nothing naming the two
 *   ends reads as a guess.
 * - **Clearing is one click**, and it is the only way back to "all time" once
 *   a date is set: a `DatePicker` on its own has no empty state to pick.
 *
 * @param {string} from        yyyy-mm-dd, or "" for unbounded
 * @param {string} to          yyyy-mm-dd, or "" for unbounded
 * @param {(range: {from: string, to: string}) => void} onChange
 * @param {boolean} [disabled] greys the whole control while a request is out
 * @param {string} [name]      distinguishes the ids when a page shows two
 *
 * `dateRangeParams` below turns what this returns into the query the endpoints
 * take — the two travel together, so they live in one file.
 */

/**
 * The convention every screen fed by this picker follows: **a picked day is
 * that day where the reader sits**, not that day in UTC.
 *
 * The picker names two days; an endpoint compares against an instant, and the
 * audit and usage tables both store UTC. So a day is turned into local
 * midnight and sent as the UTC instant that is. A reader at UTC+5:30 who picks
 * 15 Aug gets their own 15th on every screen — before this, the audit tabs
 * read the choice as local and the usage screens read it as UTC, so the two
 * windows sat 5½ hours apart over the same pair of dates.
 *
 * `dateRangeParams` below and `windowParams` in
 * `components/usage/useTenantUsage.js` are the only two callers. Endpoints
 * differ on whether the upper bound is inclusive, so each picks its own end:
 * `localDayEnd` for an inclusive one, `localDayAfter` for an exclusive one.
 *
 * @param {string} day        yyyy-mm-dd
 * @param {number} offsetDays days to add before taking midnight
 */
const localMidnight = (day, offsetDays = 0) => {
  const [y, m, d] = String(day).split("-").map(Number);
  return new Date(y, m - 1, d + offsetDays, 0, 0, 0, 0);
};

/** First instant of a picked day, local, as a UTC ISO string. */
export const localDayStart = (day) => localMidnight(day).toISOString();

/** Last instant of a picked day — for an endpoint whose end is inclusive. */
export const localDayEnd = (day) => new Date(localMidnight(day, 1).getTime() - 1).toISOString();

/** Midnight after a picked day — for an endpoint whose end is exclusive. */
export const localDayAfter = (day) => localMidnight(day, 1).toISOString();

/**
 * A picked window as the `fromDate` / `toDate` the audit endpoints take. Both
 * bounds are inclusive there.
 *
 * Sending the raw `yyyy-mm-dd` was the bug this replaces: `new Date("2026-08-15")`
 * is midnight, so a To of the 15th excluded everything that happened *on* the
 * 15th, and picking one single day returned nothing at all.
 *
 * Returns only the keys that are set, so it spreads straight into a params
 * object without planting `undefined` entries.
 */
export const dateRangeParams = ({ from, to } = {}) => {
  const params = {};
  if (from) params.fromDate = localDayStart(from);
  if (to) params.toDate = localDayEnd(to);
  return params;
};

const DateRangeFilter = ({ from = "", to = "", onChange, disabled = false, name = "range", className = "" }) => {
  const hasWindow = !!from || !!to;

  const field = (which, value, bounds) => (
    <div className="flex items-center gap-1.5">
      <label className="text-[11px] font-medium tracking-wide text-text-secondary" htmlFor={`${name}-${which}`}>
        {which === "from" ? "From" : "To"}
      </label>
      {/* Its own field box, a size down from the group's outer one
          (`rounded-md` inside `rounded-lg`), so the group reads as one
          control holding two, not one flat box the two blend into. */}
      <DatePicker
        id={`${name}-${which}`}
        value={value}
        onChange={(next) => onChange({ from, to, [which]: next })}
        disabled={disabled}
        placeholder="Any"
        ariaLabel={which === "from" ? "Start of the date range" : "End of the date range"}
        buttonClassName="h-6 w-auto min-w-[62px] gap-1.5 whitespace-nowrap rounded-md px-1.5 text-xs border-border-primary bg-background-primary"
        {...bounds}
      />
    </div>
  );

  return (
    <div className={cn("flex items-center gap-2.5 h-8 rounded-lg border border-border-input bg-background-form px-3", disabled && "opacity-60", className)}>
      {field("from", from, { maxDate: to || undefined })}
      {field("to", to, { minDate: from || undefined })}

      {hasWindow && (
        <Button
          type="button"
          onClick={() => onChange({ from: "", to: "" })}
          disabled={disabled}
          aria-label="Clear the date range"
          title="Clear the date range"
          className="flex h-6 w-6 items-center justify-center rounded-md p-0 text-text-secondary hover:bg-background-tertiary hover:text-text-primary disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
};

export default DateRangeFilter;
